import { useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";

import type { Artifact, Citation } from "@/types/eryndal";
import { answerToMarkdown } from "@/utils/artifactMarkdown";
import { cn } from "@/utils/cn";

import { CitationsPanel } from "./CitationsPanel";

/** 32px round avatar, per the spec's message rows. */
function Avatar({ variant }: { variant: "user" | "agent" }) {
  if (variant === "agent") {
    return (
      <span
        aria-hidden
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-oxblood-500 font-display text-[0.8rem] text-parchment-50"
      >
        E
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-ruleline bg-parchment-50 font-mono text-[0.66rem] text-warmgray-500"
    >
      SB
    </span>
  );
}

/**
 * Numbered source chips under the answer. A citation with a URL points out of
 * the corpus; one without came from the graph or a document already in scope,
 * and the spec gives those the alarm wash so provenance reads at a glance.
 */
function CitationChips({ citations }: { citations: Citation[] }) {
  if (!citations.length) return null;
  return (
    <div className="animate-ery-rise space-y-2">
      <div className="label-mono-muted">Sources</div>
      <ul data-testid="citation-chips" className="flex flex-wrap gap-2">
        {citations.map((c, idx) => {
          const label = c.title ?? c.ref ?? c.id ?? "Unnamed source";
          const isExternal = Boolean(c.url);
          const chipClass = isExternal ? "chip-citation" : "chip-citation-internal";
          const inner = (
            <>
              <span
                className={cn(
                  "h-[5px] w-[5px] shrink-0 rounded-full",
                  isExternal ? "bg-warmgray-300" : "bg-oxblood-400",
                )}
              />
              <span className="font-mono text-[0.62rem] text-oxblood-400">{idx + 1}</span>
              <span className="max-w-[22ch] truncate">{label}</span>
            </>
          );
          return (
            <li key={c.id ?? c.ref ?? idx}>
              {c.url ? (
                <a className={chipClass} href={c.url} rel="noreferrer noopener" target="_blank">
                  {inner}
                </a>
              ) : (
                <span className={chipClass}>{inner}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AnswerActions({
  query,
  answer,
  index,
  artifacts,
  citations,
  groundingScore,
}: {
  query: string;
  answer: string;
  index: number;
  artifacts: Artifact[];
  citations: Citation[];
  groundingScore?: number | null;
}) {
  const [copied, setCopied] = useState(false);

  const filename = `eryndal-answer-${String(index).padStart(2, "0")}.md`;

  // Artifacts render only in the drawer now, so an export of just the prose
  // would silently omit everything the run built. Both actions hand over the
  // whole document — answer, artifacts as real markdown, numbered sources.
  const document_ = () =>
    answerToMarkdown({ query, answer, artifacts, citations, groundingScore });

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(document_());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  const onDownload = () => {
    const blob = new Blob([document_()], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-wrap items-center gap-4 border-t border-ruleline pt-3">
      <button
        type="button"
        onClick={onCopy}
        className="font-mono text-[0.7rem] text-oxblood-500 transition-colors duration-200 ease-atelier hover:text-oxblood-600"
      >
        {copied ? "copied ✓" : "copy"}
      </button>
      <button
        type="button"
        onClick={onDownload}
        className="font-mono text-[0.7rem] text-oxblood-500 transition-colors duration-200 ease-atelier hover:text-oxblood-600"
      >
        download .md ↓
      </button>
      {groundingScore !== undefined && groundingScore !== null ? (
        <span className="font-mono text-[0.7rem] text-warmgray-400">
          grounding {groundingScore.toFixed(2)}
        </span>
      ) : null}
    </div>
  );
}

interface Props {
  index: number;
  query: string;
  answer: string;
  citations?: Citation[];
  /**
   * Export only. Artifacts render in the shell drawer, never in the column —
   * they are here so "copy" and "download .md" produce a complete document.
   */
  artifacts?: Artifact[];
  groundingScore?: number | null;
  isStreaming?: boolean;
  /**
   * Rendered directly under the agent's name label, above the answer — the
   * reasoning belongs with the agent that produced it, not appended after the
   * turn. A slot rather than the entries themselves, so ChatTurn stays
   * independent of ThinkingPanel's types.
   */
  thinkingSlot?: ReactNode;
  className?: string;
}

export function ChatTurn({
  index,
  query,
  answer,
  citations = [],
  artifacts = [],
  groundingScore,
  isStreaming = false,
  thinkingSlot,
  className,
}: Props) {
  return (
    <article className={cn("space-y-6 border-b border-ruleline pb-10", className)}>
      <div className="flex gap-3.5">
        <Avatar variant="user" />
        <div className="min-w-0 flex-1">
          <div className="label-mono-muted">You</div>
          <div className="mt-1.5 inline-block max-w-[80%] rounded-[14px_14px_14px_4px] bg-espresso-700 px-[17px] py-[13px] font-serif text-[0.95rem] leading-[1.55] text-parchment-100">
            {query}
          </div>
        </div>
      </div>

      <div className="flex gap-3.5">
        <Avatar variant="agent" />
        <div className="min-w-0 flex-1">
          <div className="label-mono">Eryndal</div>

          {/* Two explicit rows. The top one is the pair of summary bars —
              reasoning on the left, citations in the rail — so they line up
              and together span the text column. The answer then spans BOTH
              columns beneath them: reserving the rail for its whole height
              left the answer at about half width with a column of empty
              space beside it.

              Placement is explicit rather than by source order, because
              either bar can be absent (no thinking events, no citations) and
              the remaining one must still land in its own column. */}
          <div className="mt-2 grid gap-x-6 gap-y-5 lg:grid-cols-[1fr_theme(spacing.rail)]">
            {thinkingSlot ? (
              <div className="min-w-0 lg:col-start-1 lg:row-start-1">{thinkingSlot}</div>
            ) : null}

            {/* Last on narrow screens: a source list above the answer it
                belongs to reads backwards. */}
            <CitationsPanel
              citations={citations}
              className="order-last lg:order-none lg:col-start-2 lg:row-start-1"
            />

            <div className="min-w-0 space-y-5 lg:col-span-2 lg:row-start-2">
              <div className="prose-eryndal">
                {answer ? (
                  <>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                      {answer}
                    </ReactMarkdown>
                    {isStreaming ? (
                      <span
                        aria-hidden
                        className="ml-0.5 inline-block h-4 w-2 animate-ery-caret bg-oxblood-400 align-text-bottom"
                      />
                    ) : null}
                  </>
                ) : (
                  <p className="font-serif italic text-warmgray-400">
                    {isStreaming ? "Drafting…" : "No answer yet."}
                  </p>
                )}
              </div>

              {answer && !isStreaming ? (
                <>
                  <CitationChips citations={citations} />
                  <AnswerActions
                    query={query}
                    answer={answer}
                    index={index}
                    artifacts={artifacts}
                    citations={citations}
                    groundingScore={groundingScore}
                  />
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
